import TrackingUtils from "./TrackingUtils";
import RequestUtil from "../utils/RequestUtil";
import { getScreenType } from "../utils/BannerUtils";
import FeatureSessionStore from "../stores/FeatureSessionStore";
import { trackStructuredEvent, trackForGtmOnly, getCurrentSubContextId } from "./TrackingBase";
import type { Banner } from "../utils/BannerUtils";
import type { BannerType, PageType, TargetParams } from "../utils/BannerContainer";
import type { CategoryPath } from "./WebAnalyticsTrackingUtil";
import { getGA360DeliveryModeName, getGA360FulfillmentTypeName } from "../utils/GA360Fields";
let currentPageName = "";
const _impression = "impression";
const _click = "click";
const _componentView = "componentView";
type Kpi = "impression" | "click" | "sponsored.click";
type KpiExtended = Kpi | "componentView";

const getEnhancedErrorInformation = function () {
	let clientLocation = "";

	if (typeof window !== "undefined" && window && window.location) {
		clientLocation = window.location.href;
	}

	return {
		clientLocation: clientLocation,
		currentPageName: currentPageName,
	};
};

const trackHookViaGtm = function (state) {
	if (state && state.query && (state.query.cid || state.query.icid)) {
		trackForGtmOnly(
			"platform",
			"ui",
			"stateUpdate",
			{
				stateOf: state.query.cid ? "cid" : "icid",
			},
			{
				cid: state.query.cid,
				icid: state.query.icid,
			},
		);
	}
};

const trackHookViaStructuredTracking = function (state) {
	if (state && state.routeParams && !state.routeParams.doNotTrackInHook) {
		currentPageName = TrackingUtils.getPageName(state);
		const orderId = state?.params?.orderId;
		const tags = {
			pageName: currentPageName,
			templateType: currentPageName,
			sourceRouterPath: state.path,
			sourceRouterQuery: RequestUtil.buildQuery(state.query),
			sourcePath: window.location.pathname,
			sourceHost: window.location.hostname,
			sourceQuery: window.location.search,
			orderId: TrackingUtils.hash.calculate(orderId),
		};
		const structuredData = {
			tags,
		};
		trackStructuredEvent("platform", "ui", "page", structuredData);
	}
};

const trackingHook = function (state) {
	trackHookViaGtm(state);
	trackHookViaStructuredTracking(state);
	return {
		checkNext: true,
		didRedirect: false,
	};
};

const trackSearchEvent = function () {
	console.log("Adobe omniture is deprecated");
};

const trackPurchaseEvent = function () {
	console.log("Adobe omniture is deprecated");
};

const trackBannerImpression = function (
	banner: Banner,
	targetParams: TargetParams | null | undefined,
	pageType: PageType,
) {
	trackBannerKpi(_impression, banner, targetParams, pageType);
};

const trackBannerClick = function (banner: Banner, targetParams: TargetParams | null | undefined, pageType: PageType) {
	trackBannerKpi(_click, banner, targetParams, pageType);
};

const trackFooterKpi = function (kpi: KpiExtended) {
	const tags = {
		page: TrackingUtils.getPageForFooter(),
	};
	trackStructuredEvent("platform", "ui", "footer." + kpi, {
		tags,
	});
};

const trackFooterImpression = function () {
	trackFooterKpi(_impression);
};

const trackFooterClick = function () {
	trackFooterKpi(_click);
};

const trackFooterComponentView = function () {
	trackFooterKpi(_componentView);
};

const trackFooterError = function () {
	trackStructuredEvent("platform", "ui", "footer.error");
};

type PageImpressionPayload = {
	categoryPath?: CategoryPath | null | undefined;
	displayedFulfillmentTypes?: Array<string> | null | undefined;
	customerBuyable?: boolean | null | undefined;
	employeeBuyable?: boolean | null | undefined;
	selectedDeliveryModeField?: string;
	availableDeliveryModesField?: Array<string> | null | undefined;
};

const trackPageImpressionWithPayload = function (
	pageTitle: string | null | undefined,
	templateType: string,
	payload: PageImpressionPayload,
) {
	const availableDeliveryModes: string | null | undefined =
		payload.availableDeliveryModesField && payload.availableDeliveryModesField.length > 0
			? payload.availableDeliveryModesField.map(getGA360DeliveryModeName).sort().join("_")
			: null;
	const selectedDeliveryMode: string | null | undefined =
		payload.selectedDeliveryModeField !== undefined
			? getGA360DeliveryModeName(payload.selectedDeliveryModeField)
			: null;
	const additionalTags = {
		customerBuyable: payload.customerBuyable,
		employeeBuyable: payload.employeeBuyable,
		selectedDeliveryMode: selectedDeliveryMode,
		availableDeliveryModes: availableDeliveryModes,
	};
	trackPageImpression(
		pageTitle,
		templateType,
		payload.categoryPath,
		payload.displayedFulfillmentTypes,
		additionalTags,
	);
};

const trackPageImpression = function (
	pageTitle: string | null | undefined,
	templateType: string,
	categoryPath?: CategoryPath | null | undefined,
	displayedFulfillmentTypes?: Array<string> | null | undefined,
	additionalTags?: Record<string, any>,
) {
	const { customerValidForFsd, customerValidForWeb } = TrackingUtils.getCustomerInformation() || {};
	const customerTypeStrings: string[] = [];

	if (customerValidForFsd) {
		customerTypeStrings.push("fsd");
	}

	if (customerValidForWeb) {
		customerTypeStrings.push("web");
	}

	const customerType: string = customerTypeStrings.join("_");
	const shopChannel: string | null | undefined = mapToShopChannel(displayedFulfillmentTypes);
	const tags = {
		pageHeadline: pageTitle,
		templateType: templateType,
		categoryPath: categoryPath,
		sourcePath: window.location.pathname,
		sourceHost: window.location.hostname,
		sourceQuery: window.location.search,
		href: window.location.href,
		referrer: document.referrer,
		shopChannel,
		customerType,
		...additionalTags,
	};
	trackStructuredEvent("platform", "ui", "page.impression", {
		tags,
	});
};

const mapToShopChannel = function (fulfillmentTypes: Array<string> | null | undefined): string | null | undefined {
	return fulfillmentTypes && fulfillmentTypes.length > 0
		? fulfillmentTypes.map(getGA360FulfillmentTypeName).sort().join("_")
		: null;
};

const trackArticleBannerClick = function (
	banner: Banner,
	targetParams: TargetParams | null | undefined,
	pageType: PageType,
	articleId: string,
) {
	trackArticleBanner(_click, banner, targetParams, pageType, [articleId]);
};

const trackArticleBannerSponsoredClick = function (
	banner: Banner,
	targetParams: TargetParams | null | undefined,
	pageType: PageType,
	articleId: string,
) {
	trackArticleBanner("sponsored.click", banner, targetParams, pageType, [articleId]);
};

const trackArticleBannerImpression = function (
	banner: Banner,
	targetParams: TargetParams | null | undefined,
	pageType: PageType,
	articleIds: Array<string>,
) {
	trackArticleBanner(_impression, banner, targetParams, pageType, articleIds);
};

const trackArticleBanner = function (
	kpi: Kpi,
	banner: Banner,
	targetParams: TargetParams | null | undefined,
	pageType: PageType,
	bettyBundleIds: Array<string>,
) {
	const tags = {
		kpi: kpi,
		campaignId: banner.campaignId,
		campaignName: banner.campaignName,
		placementId: banner.placementId,
		placementName: banner.placementName,
		bannerType: banner.type,
		page: banner.page,
		customerTargetGroup: targetParams?.customerTargetGroup,
		customerCategoryGroup: targetParams?.customerCategoryGroup,
		customerType: targetParams?.customerType,
		categoryId: targetParams?.categoryId,
		categoryPathIds: targetParams?.categoryPathId?.join(","),
		customerId: targetParams?.customerId,
		storelistId: targetParams?.storelistId,
		store: TrackingUtils.getSessionStoreInfo().store,
		orderType: banner.orderType,
		articleIds: bettyBundleIds,
		bannerPageType: pageType,
	};
	const orderHabitFeatureToggle = FeatureSessionStore.getMembershipFor("ENABLE_ORDER_HABITS");

	if (orderHabitFeatureToggle === "B") {
		Object.assign(tags, {
			customerId: TrackingUtils.getSessionStoreInfo().customerId,
		});
	}

	// if we are in an employee context, dont track banner kpis
	if (TrackingUtils.getSessionStoreInfo().employeeId === "") {
		trackStructuredEvent("platform", "ui", "banner." + kpi, {
			tags,
		});
	}
};

const trackBannerKpi = function (
	kpi: Kpi,
	banner: Banner,
	targetParams: TargetParams | null | undefined,
	pageType: PageType,
) {
	const tags = {
		kpi: kpi,
		campaignId: banner.campaignId,
		campaignName: banner.campaignName,
		placementId: banner.placementId,
		placementName: banner.placementName,
		creativeId: banner.creativeId,
		bannerType: banner.type,
		page: banner.page,
		targetLink: banner.link,
		targetLinkWithinMShop: TrackingUtils.getBannerTargetLinkWithinMShop(banner.link, banner.linkType),
		advertisement: banner.advertisement,
		screenType: getScreenType(),
		categoryId: targetParams?.categoryId,
		categoryPathIds: targetParams?.categoryPathId?.join(","),
		customerTargetGroup: targetParams?.customerTargetGroup,
		customerCategoryGroup: targetParams?.customerCategoryGroup,
		customerType: targetParams?.customerType,
		customerId: targetParams?.customerId,
		storelistId: targetParams?.storelistId,
		orderType: banner.orderType,
		store: TrackingUtils.getSessionStoreInfo().store,
		bannerPageType: pageType,
	};
	const orderHabitFeatureToggle = FeatureSessionStore.getMembershipFor("ENABLE_ORDER_HABITS");

	if (orderHabitFeatureToggle === "B") {
		Object.assign(tags, {
			customerId: TrackingUtils.getSessionStoreInfo().customerId,
		});
	}

	// if we are in an employee context, dont track banner kpis
	if (TrackingUtils.getSessionStoreInfo().employeeId === "") {
		trackStructuredEvent("platform", "ui", "banner." + kpi, {
			tags,
		});
	}
};

const trackBannerError = function (
	pageType: PageType,
	bannerType: BannerType,
	targetParams: TargetParams | null | undefined,
) {
	const tags = {
		kpi: "error",
		bannerType: bannerType,
		page: pageType,
		targetParams: JSON.stringify(targetParams || {}),
	};
	trackStructuredEvent("platform", "ui", "banner.error", {
		tags,
	});
};

const trackLogoutEvent = function () {
	// Adobe omniture is deprecated therefore the call to the tracking pixel was removed
	trackStructuredEvent("platform", "ui", "app.logout", {});
};

//Type is either customer or employee
const trackLoginEvent = function () {
	// Adobe omniture is deprecated therefore the call to the tracking pixel was removed
	trackStructuredEvent("platform", "ui", "app.login", {});
};

const trackChangingMinStableUIVersion = function (
	currentTimestamp: string | null | undefined,
	newTimestamp: string | null | undefined,
) {
	const tags = {
		currentTimestamp,
		newTimestamp,
	};
	trackStructuredEvent("platform", "ui", "technical.client.minStableUIVersionChanged", {
		tags,
	});
};

const trackAnonymous = function () {
	trackStructuredEvent("platform", "ui", "technical.client.newAnonUserId", {});
};

const trackUIError = function (vertical, service, component, message, exception) {
	if (!exception) {
		trackError(vertical, service, component, message);
		console.warn("Error in component " + component + ": " + message);
	} else {
		trackError(vertical, service, component, message, {
			exception: exception.stack ? exception.stack : JSON.stringify(exception),
		});
		console.warn("Error in component " + component + ": " + message, exception);
	}
};

const trackClientErrorEvent = customParameters => {
	const additionalData = { ...customParameters, ...getEnhancedErrorInformation() };
	trackError("platform", "ui", customParameters.component, customParameters.message, additionalData);
};

const trackError = (vertical, service, component, message, anyData?) => {
	trackStructuredEvent(vertical, service, "technical.client.error", {
		tags: { ...anyData, ...getEnhancedErrorInformation(), message: message, component: component },
	});
};

const trackJwtRefreshEvent = (vertical, service, component, message, anyData) => {
	trackStructuredEvent(vertical, service, "jwtRefresh", {
		tags: { ...anyData, ...getEnhancedErrorInformation(), message, component },
	});
};

const trackTrackingError = function (funcName, funcArgs, error) {
	const errorInfo = {
		funcName: funcName,
		funcArgs: funcArgs,
		errorName: error.name,
		errorMsg: error.message,
	};
	trackStructuredEvent("platform", "ui", "technical.tracking.error", {
		tags: errorInfo,
	});
};

const tryCatch = function (funcName: string, func: (...args: any[]) => any) {
	return function () {
		try {
			//@ts-expect-error: 'this' type problem
			return func.apply(this, arguments);
		} catch (err) {
			try {
				console.warn("Error in tracking call '" + funcName + "':", err);
				trackTrackingError(funcName, arguments, err);
			} catch {
				/* Avoiding this tracking call to throw an exception too */
			}
		}
	};
};

export default {
	hook: tryCatch("hook", trackingHook) as (...args: Array<any>) => any,
	mapToShopChannel: tryCatch("mapToShopChannel", mapToShopChannel) as (...args: Array<any>) => any,
	purchaseEvent: tryCatch("purchaseEvent", trackPurchaseEvent) as (...args: Array<any>) => any,
	searchEvent: tryCatch("searchEvent", trackSearchEvent) as (...args: Array<any>) => any,
	trackLogoutEvent: tryCatch("trackLogoutEvent", trackLogoutEvent) as (...args: Array<any>) => any,
	trackLoginEvent: tryCatch("trackLoginEvent", trackLoginEvent) as (...args: Array<any>) => any,
	trackChangingMinStableUIVersion: tryCatch("trackChangingMinStableUIVersion", trackChangingMinStableUIVersion) as (
		...args: Array<any>
	) => any,
	trackClientErrorEvent: tryCatch("trackClientErrorEvent", trackClientErrorEvent) as (...args: Array<any>) => any,
	trackAnonymous: tryCatch("trackAnonymous", trackAnonymous) as (...args: Array<any>) => any,
	trackUIError: tryCatch("trackUIError", trackUIError) as (...args: Array<any>) => any,
	trackForGtmOnly: tryCatch("trackForGtmOnly", trackForGtmOnly) as (...args: Array<any>) => any,
	trackStructuredEvent: tryCatch("trackStructuredEvent", trackStructuredEvent) as (...args: Array<any>) => any,
	trackBannerImpression: tryCatch("trackBannerImpression", trackBannerImpression) as (...args: Array<any>) => any,
	trackPageImpression: tryCatch("trackPageImpression", trackPageImpression) as (...args: Array<any>) => any,
	trackPageImpressionWithPayload: tryCatch("trackPageImpressionWithPayload", trackPageImpressionWithPayload) as (
		...args: Array<any>
	) => any,
	trackBannerClick: tryCatch("trackBannerClick", trackBannerClick) as (...args: Array<any>) => any,
	trackArticleBannerClick: tryCatch("trackArticleBannerClick", trackArticleBannerClick) as (
		...args: Array<any>
	) => any,
	trackArticleBannerSponsoredClick: tryCatch(
		"trackArticleBannerSponsoredClick",
		trackArticleBannerSponsoredClick,
	) as (...args: Array<any>) => any,
	trackArticleBannerImpression: tryCatch("trackArticleBannerImpression", trackArticleBannerImpression) as (
		...args: Array<any>
	) => any,
	trackBannerError: tryCatch("trackBannerError", trackBannerError) as (...args: Array<any>) => any,
	trackError: tryCatch("trackError", trackError) as (...args: Array<any>) => any,
	trackJwtRefreshEvent: tryCatch("trackJwtRefreshEvent", trackJwtRefreshEvent) as (...args: Array<any>) => any,
	trackArticleBanner: tryCatch("trackArticleBanner", trackArticleBanner) as (...args: Array<any>) => any,
	trackBannerKpi: tryCatch("trackBannerKpi", trackBannerKpi) as (...args: Array<any>) => any,
	trackFooterKpi: tryCatch("trackFooterKpi", trackFooterKpi) as (...args: Array<any>) => any,
	trackFooterImpression: tryCatch("trackFooterImpression", trackFooterImpression) as (...args: Array<any>) => any,
	trackFooterClick: tryCatch("trackFooterClick", trackFooterClick) as (...args: Array<any>) => any,
	trackFooterComponentView: tryCatch("trackFooterComponentView", trackFooterComponentView) as (
		...args: Array<any>
	) => any,
	trackFooterError: tryCatch("trackFooterError", trackFooterError) as (...args: Array<any>) => any,
	getCurrentSubContextId: tryCatch("getCurrentSubContextId", getCurrentSubContextId) as (...args: Array<any>) => any,
	// for testing only
	_try_catch: tryCatch as (...args: Array<any>) => any,
};
